import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Modal } from "react-bootstrap";
import toast from "react-hot-toast";
import Box from "../../../../components/Box";
import { Helpers } from "../../../../helpers/Helpers";
import { KTCard } from "../../../../_metronic/helpers/components/KTCard";
import { KTCardBody } from "../../../../_metronic/helpers/components/KTCardBody";
import Content from "../../../../_metronic/layout/components/content/Content";
import { ToolbarWrapper } from "../../../../_metronic/layout/components/toolbar";
import { PageTitle } from "../../../../_metronic/layout/core";
import { storeActivityIndicator } from "../../../../redux/reducers/authSlice";
import CustomSingleSelect, { CustomSingleSelectModel } from "../../../../components/CustomSingleSelect";
import CustomNumberInput from "../../../../components/CustomNumberInput";
import CustomTextInput from "../../../../components/CustomTextInput";
import CustomDatePicker from "../../../../components/CustomDatePicker";
import { CompanyService } from "../../../../services/CompanyService";
import { fiyatYaz, sayiYaz } from "../../../../helpers/yakitHesap";
import { YAKIT_SAYFASI, YakitKaydetSonucu, YakitKaydi, YakitPlakaSecenegi, YakitSecenekleri, YakitSeferSecenegi, YakitService } from "../../../../services/YakitService";
import YakitSatirlari, { YakitSatirModeli } from "./Components/YakitSatirlari";

const bugun = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const sayi = (s: string | undefined) => { const n = Number(s); return s == null || s === '' || isNaN(n) ? 0 : n; }

/** Seferin seçim değeri "modül|id" (iki modülün seferi tek listede). */
const seferDegeri = (modul: number, id: string) => `${modul}|${id}`;

/**
 * Genel Giderler > Yakıt Kayıtları > kayıt (27.10.2026, #490; tasarım docs/yakit-kayitlari-asama2-tasarim.md).
 * A1 Yeni kayıt: tarih bugün, döviz TL, detay gridi boş ("Ekle" ile satır girilir).
 * R1 Plaka seçilince araç cinsi ve aracın sürücüsü dolar; sefer listesi aracın seferlerini önce gösterir.
 * R2 Detay gridi standart kalıp (Ekle / Güncelle / Sil + form); hesap netten ya da brütten (YakitSatirlari).
 * R3 Belge no alanından çıkınca mükerrer kontrolü; kayıtta olası mükerrer → "Yine de kaydet" onayı.
 * R4 Sefer başka araca aitse uyarı: kayıt yapılır, sefer bağı incelemeye alınır.
 * R5 Sunucu satırları yeniden hesaplar; fark 1 kuruşu geçerse kaydı reddeder.
 */
const YakitAdd = () => {
    const navigate = useNavigate();
    const pageName = YAKIT_SAYFASI
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();
    const anahtarSayaci = useRef(1);

    const [secenekler, setSecenekler] = useState<YakitSecenekleri | undefined>(undefined);
    const [plakalar, setPlakalar] = useState<YakitPlakaSecenegi[]>([]);
    const [kayit, setKayit] = useState<YakitKaydi | undefined>(undefined);

    const [belgeNo, setBelgeNo] = useState<string | undefined>(undefined);
    const [islemTarihi, setIslemTarihi] = useState<string | undefined>(bugun());
    const [islemSaati, setIslemSaati] = useState<string>('');
    const [firma, setFirma] = useState<CustomSingleSelectModel | undefined>(undefined);
    const [doviz, setDoviz] = useState<CustomSingleSelectModel | undefined>(undefined);
    const [aciklama, setAciklama] = useState<string | undefined>(undefined);
    const [arac, setArac] = useState<CustomSingleSelectModel | undefined>(undefined);
    const [surucu, setSurucu] = useState<CustomSingleSelectModel | undefined>(undefined);
    const [sefer, setSefer] = useState<CustomSingleSelectModel | undefined>(undefined);
    const [seferUyarisi, setSeferUyarisi] = useState<string | undefined>(undefined);
    const [kilometre, setKilometre] = useState<string | undefined>(undefined);
    const [satirlar, setSatirlar] = useState<YakitSatirModeli[]>([]);
    const [mukerrerUyarisi, setMukerrerUyarisi] = useState<string | undefined>(undefined);
    const [onayPenceresi, setOnayPenceresi] = useState<string | undefined>(undefined);
    const [uyarilar, setUyarilar] = useState<string[]>([]);

    const seferListesi = useRef<YakitSeferSecenegi[]>([]);

    useEffect(() => {
        baslat(searchParams.get('id'));
    }, [])

    const baslat = async (id: string | null) => {
        try {
            dispatch(storeActivityIndicator(true));

            var s = await YakitService.secenekler();
            if (!s.isSuccess) {
                toast.error(s.message ?? '');
                return;
            }
            const sec = s.data as YakitSecenekleri;
            setSecenekler(sec);

            var p = await YakitService.plakalar();
            if (p.isSuccess)
                setPlakalar(p.data as YakitPlakaSecenegi[]);

            if (!id) {
                const tl = sec.dovizCinsleri.find(x => x.value == sec.varsayilanDovizId);
                if (tl)
                    setDoviz({ label: tl.label, value: tl.value });
                return;
            }

            var response = await YakitService.getById(id);
            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }
            doldur(response.data as YakitKaydi);
        } catch (error: any) {
            toast.error(error);
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    const doldur = (k: YakitKaydi) => {
        setKayit(k);
        setBelgeNo(k.belgeNo);
        setIslemTarihi(k.islemTarihiDeger);
        setIslemSaati(k.islemSaati ?? '');
        setFirma(k.tedarikciId ? { label: k.yakitFirmasi ?? '', value: k.tedarikciId } : undefined);
        setDoviz({ label: k.dovizCinsi ?? '', value: k.currencyTypeId });
        setAciklama(k.aciklama ?? undefined);
        setArac({ label: k.plaka ?? '', value: k.vehicleId, additionalValue1: k.aracCinsi });
        setSurucu(k.driverId ? { label: k.surucu ?? '', value: k.driverId } : undefined);
        setSefer(k.seferId && k.seferModulu ? { label: k.seferNo ?? '', value: seferDegeri(k.seferModulu, k.seferId) } : undefined);
        setSeferUyarisi(k.seferDurumu == 'İnceleniyor' ? 'Bu fişin sefer bağı inceleniyor (seferin aracı fişin aracından farklı ya da boş).' : undefined);
        setKilometre(k.kilometre != null ? String(k.kilometre) : undefined);
        setSatirlar(k.satirlar.map(x => ({
            anahtar: anahtarSayaci.current++,
            yakitTuru: { label: x.yakitTuru ?? '', value: x.yakitTuruId },
            birim: { label: x.birim ?? '', value: x.birimId },
            kdvOrani: { label: '%' + x.kdvOrani, value: x.kdvOraniId, additionalValue1: String(x.kdvOrani) },
            miktar: x.miktar,
            birimFiyat: x.birimFiyat,
            net: x.net,
            kdv: x.kdv,
            brut: x.brut,
            hesapYonu: x.hesapYonu,
            not: x.not ?? undefined
        })));
    }

    const liste = (al: (s: YakitSecenekleri) => { value: string, label: string, ek?: string }[]) =>
        () => Promise.resolve({ isSuccess: true, data: secenekler ? al(secenekler).map(x => ({ label: x.label, value: x.value, ek: x.ek })) : [] });

    const dovizEki = doviz && doviz.label != 'TRY' && doviz.label != 'TL' ? ' ' + doviz.label : '';

    // ---- R1: araç seçimi
    const aracSecildi = (v: CustomSingleSelectModel | undefined) => {
        setArac(v);
        setSefer(undefined);
        setSeferUyarisi(undefined);
        const p = plakalar.find(x => x.value == v?.value);
        if (p?.varsayilanSurucuId)
            setSurucu({ label: p.varsayilanSurucu ?? '', value: p.varsayilanSurucuId });
    }

    // ---- R4: sefer seçimi
    const seferleriGetir = async (arama: string | undefined) => {
        var r = await YakitService.seferler(arac?.value, arama);
        if (!r.isSuccess)
            return r;
        seferListesi.current = r.data as YakitSeferSecenegi[];
        return {
            isSuccess: true,
            data: seferListesi.current.map(x => ({
                value: seferDegeri(x.modul, x.value),
                label: `${x.label} · ${x.modulAdi}${x.plaka ? ' · ' + x.plaka : ''}${x.tarihler ? ' · ' + x.tarihler : ''}`
            }))
        };
    }

    const seferSecildi = (v: CustomSingleSelectModel | undefined) => {
        setSefer(v);
        if (!v) {
            setSeferUyarisi(undefined);
            return;
        }
        const [, id] = v.value.split('|');
        const s = seferListesi.current.find(x => x.value == id);
        if (s && arac && s.vehicleId != arac.value)
            setSeferUyarisi(s.vehicleId
                ? `Sefer ${s.label} başka araca ait (${s.plaka ?? '—'}). Kayıt yapılır, sefer bağı incelemeye alınır.`
                : `Sefer ${s.label}'da araç tanımlı değil. Kayıt yapılır, sefer bağı incelemeye alınır.`);
        else if (s && surucu && s.driverId && s.driverId != surucu.value)
            setSeferUyarisi('Seferin sürücüsü fişteki sürücüden farklı.');
        else if (s && !surucu && s.driverId)
            setSeferUyarisi('Sürücü boş: kaydedince seferin sürücüsü atanır.');
        else
            setSeferUyarisi(undefined);
    }

    // ---- R3: mükerrer kontrolü
    const mukerrerKontrol = async () => {
        if (Helpers.isEmpty(belgeNo)) {
            setMukerrerUyarisi(undefined);
            return;
        }
        try {
            var r = await YakitService.mukerrerKontrol(kayit?.yakitKaydiId, belgeNo, firma?.value);
            setMukerrerUyarisi(r.isSuccess && r.data ? r.data : undefined);
        } catch { }
    }

    useEffect(() => {
        if (belgeNo)
            mukerrerKontrol();
    }, [firma])

    const kaydet = async (mukerrerOnayi: boolean = false) => {
        const eksik: string[] = [];
        if (Helpers.isEmpty(belgeNo)) eksik.push('Belge No');
        if (!islemTarihi) eksik.push('İşlem Tarihi');
        if (!arac) eksik.push('Araç Plaka');
        if (!doviz) eksik.push('Döviz');
        if (eksik.length > 0) {
            toast.error('Zorunlu alanları doldurun: ' + eksik.join(', '));
            return;
        }
        if (satirlar.length == 0) {
            toast.error('Yakıt Detayları\'na en az bir satır ekleyin.');
            return;
        }
        if (islemTarihi! > bugun()) {
            toast.error('İşlem tarihi ileri bir tarih olamaz.');
            return;
        }

        const [seferModulu, seferId] = sefer ? sefer.value.split('|') : [undefined, undefined];

        try {
            dispatch(storeActivityIndicator(true));

            var response = await YakitService.kaydet({
                id: kayit?.yakitKaydiId,
                belgeNo: belgeNo,
                islemTarihi: islemTarihi,
                islemSaati: islemSaati || undefined,
                vehicleId: arac?.value,
                driverId: surucu?.value,
                tedarikciId: firma?.value,
                kilometre: kilometre ? Math.round(sayi(kilometre)) : undefined,
                seferModulu: seferModulu ? Number(seferModulu) : undefined,
                seferId: seferId,
                currencyTypeId: doviz?.value,
                aciklama: aciklama,
                mukerrerOnayi: mukerrerOnayi,
                satirlar: satirlar.map(s => ({
                    yakitTuruId: s.yakitTuru?.value,
                    birimId: s.birim?.value,
                    miktar: s.miktar,
                    birimFiyat: s.birimFiyat,
                    kdvOraniId: s.kdvOrani?.value,
                    net: s.net,
                    kdv: s.kdv,
                    brut: s.brut,
                    hesapYonu: s.hesapYonu,
                    not: s.not
                }))
            });

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }

            const sonuc = response.data as YakitKaydetSonucu;
            if (sonuc.mukerrerOnayiGerekli) {
                setOnayPenceresi(sonuc.mukerrerMesaji ?? 'Aynı belge no başka fişte de var.');
                return;
            }

            setUyarilar(sonuc.uyarilar ?? []);
            sonuc.uyarilar?.forEach(u => toast(u, { icon: '⚠️', duration: 6000 }));
            toast.success('Yakıt kaydı kaydedildi.');

            if (sonuc.kayit) {
                const yeniKayit = !kayit;
                doldur(sonuc.kayit);
                if (yeniKayit)
                    navigate('/yakit/listadd?id=' + sonuc.kayit.yakitKaydiId, { replace: true });
            }
        } catch (error: any) {
            toast.error(error);
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    return (
        <React.Fragment>
            <ToolbarWrapper filters={undefined} />

            <PageTitle breadcrumbs={[{
                title: pageName.split('>').slice(0, pageName.split('>').length - 1).join(' > ') + ' > ',
                isActive: false,
                path: ''
            }]}>{(pageName.split('>')[pageName.split('>').length - 1])}</PageTitle>

            <Box className="mb-10">
                <Content>
                    <KTCard className="ebbe-card">
                        <KTCardBody>
                            <h4 className="mb-10 text-black">Fiş ve İşlem Bilgileri</h4>

                            <div className="row">
                                <div className="col-md-3" onBlur={mukerrerKontrol}>
                                    <CustomTextInput labelText="Belge No" isLabelRequired value={belgeNo} setValue={setBelgeNo} />
                                </div>
                                <div className="col-md-2">
                                    <CustomDatePicker labelText="İşlem Tarihi" isLabelRequired value={islemTarihi} setValue={setIslemTarihi} />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label">Saat</label>
                                    <input id="yakit-islem-saati" type="time" step={1} value={islemSaati} onChange={(e) => setIslemSaati(e.target.value)} style={{ border: '1px solid #F1F1F4' }} className="form-control form-control-solid" />
                                </div>
                                <div className="col-md-3">
                                    <CustomSingleSelect
                                        labelText="Yakıt Firması"
                                        selectedValue={firma}
                                        onChange={setFirma}
                                        onMenuOpen={() => CompanyService.getCompanyList({ isSupplier: true })}
                                        labelName='name'
                                        valueName='companyId'
                                        isOrdering
                                    />
                                </div>
                                <div className="col-md-2">
                                    <CustomSingleSelect
                                        labelText="Döviz"
                                        isLabelRequired
                                        selectedValue={doviz}
                                        onChange={setDoviz}
                                        onMenuOpen={liste(s => s.dovizCinsleri)}
                                        labelName='label'
                                        valueName='value'
                                    />
                                </div>
                            </div>

                            {mukerrerUyarisi && <div className="alert alert-light-warning py-2 mt-3 mb-0 fs-7">{mukerrerUyarisi}</div>}

                            <div className="row mt-5">
                                <div className="col-md-12">
                                    <CustomTextInput labelText="Açıklama" value={aciklama} setValue={setAciklama} />
                                </div>
                            </div>
                        </KTCardBody>
                    </KTCard>

                    <KTCard className="ebbe-card mt-10">
                        <KTCardBody>
                            <h4 className="mb-10 text-black">Araç ve Sürücü Bilgileri</h4>

                            <div className="row">
                                <div className="col-md-3">
                                    <CustomSingleSelect
                                        labelText="Araç Plaka"
                                        isLabelRequired
                                        selectedValue={arac}
                                        onChange={aracSecildi}
                                        onMenuOpen={() => Promise.resolve({ isSuccess: true, data: plakalar })}
                                        labelName='label'
                                        valueName='value'
                                        additionalValue1Name='ek'
                                    />
                                </div>
                                <div className="col-md-3">
                                    <CustomTextInput labelText="Araç Cinsi" isDisabled value={arac?.additionalValue1 ?? ''} />
                                </div>
                                <div className="col-md-3">
                                    <CustomSingleSelect
                                        labelText="Sürücü"
                                        selectedValue={surucu}
                                        onChange={setSurucu}
                                        onMenuOpen={() => YakitService.suruculer()}
                                        labelName='label'
                                        valueName='value'
                                    />
                                </div>
                                <div className="col-md-3">
                                    <CustomNumberInput labelText="Kilometre" value={kilometre} setValue={setKilometre} />
                                </div>
                            </div>

                            <div className="row mt-5">
                                <div className="col-md-6">
                                    <CustomSingleSelect
                                        labelText="Sefer No (yalnız referans)"
                                        selectedValue={sefer}
                                        onChange={seferSecildi}
                                        onMenuOpen={seferleriGetir}
                                        labelName='label'
                                        valueName='value'
                                        isPaging
                                        placeholder="Seçilen aracın seferleri üstte"
                                    />
                                </div>
                                <div className="col-md-6 d-flex align-items-end">
                                    {seferUyarisi && <div className="alert alert-light-warning py-2 mb-0 fs-7 w-100">{seferUyarisi}</div>}
                                </div>
                            </div>

                            <div className="text-muted fs-7 mt-3">Sefer yalnız referanstır: yakıt miktarı ve tutarı seferlere dağıtılmaz; seferin gerçek tüketimi ileride araç takip verisinden hesaplanır.</div>
                        </KTCardBody>
                    </KTCard>

                    <YakitSatirlari
                        satirlar={satirlar}
                        setSatirlar={setSatirlar}
                        secenekler={secenekler}
                        dovizEki={dovizEki}
                        yeniAnahtar={() => anahtarSayaci.current++}
                    />

                    {
                        (uyarilar.length > 0 || (kayit && kayit.kur != 1)) && (
                            <KTCard className="ebbe-card mt-10">
                                <KTCardBody>
                                    {kayit && kayit.kur != 1 && <div className="fs-7 mb-2">TL karşılığı (işlem tarihi döviz alış kuru {fiyatYaz(kayit.kur)}): <b>{sayiYaz(kayit.toplamBrutTL)} TL</b></div>}
                                    {uyarilar.length > 0 && <div className="alert alert-light-warning py-2 mb-0 fs-7">{uyarilar.map((u, i) => <div key={i}>{u}</div>)}</div>}
                                </KTCardBody>
                            </KTCard>
                        )
                    }

                    <KTCard className="ebbe-card mt-10">
                        <KTCardBody>
                            <Box flexDirection="row" justifyContent="flex-end">
                                <Box mr="15px">
                                    <button className="btn btn-sm btn-light btn-active-light-primary" onClick={() => navigate(Helpers.getGoBackUrl())}>Geri</button>
                                </Box>
                                <Box>
                                    <button className="btn btn-primary btn-sm" onClick={() => kaydet(false)}>Kaydet</button>
                                </Box>
                            </Box>
                        </KTCardBody>
                    </KTCard>
                </Content>
            </Box>

            <Modal show={!!onayPenceresi} onHide={() => setOnayPenceresi(undefined)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Aynı belge no</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">{onayPenceresi}</div>
                    <div className="text-muted fs-7">Farklı bir istasyonun aynı numaralı fişiyse kaydedebilirsiniz; kayıtta "aynı belge no" işareti kalır.</div>
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-sm btn-light" onClick={() => setOnayPenceresi(undefined)}>Vazgeç</button>
                    <button className="btn btn-sm btn-warning" onClick={() => { setOnayPenceresi(undefined); kaydet(true); }}>Yine de kaydet</button>
                </Modal.Footer>
            </Modal>
        </React.Fragment>
    )
}

export default YakitAdd;
